<template>
  <div class="column bg_page add_outbound_campaign_page full_page">
    <!-- Server error alert -->
    <div v-if="serverErrorMessage" class="server_error_alert">
      <div class="error_alert_content">
        <fluent-icon icon="warning" />
        <div class="error_messages">
          <p v-if="serverErrorMessage.length === 1" class="error_text">
            {{ serverErrorMessage[0] }}
          </p>
          <ul v-else class="error_list">
            <li v-for="(msg, index) in serverErrorMessage" :key="index">
              {{ msg }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="content-box page_height">
      <section class="outbound_campaign_add_section">
        <aside>
          <InputCard
            :label="
              $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CAMPAIGN_NAME.LABEL')
            "
            class="outbound_input_field"
          >
            <InputField
              v-model="outboundCampaign.campaignName"
              type="text"
              :disabled="isEditMode"
              :placeholder="
                $t(
                  'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CAMPAIGN_NAME.PLACEHOLDER'
                )
              "
              :error="
                $v.outboundCampaign.campaignName.$error
                  ? $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CAMPAIGN_NAME.ERROR'
                    )
                  : ''
              "
              @blur="$v.outboundCampaign.campaignName.$touch"
            />
          </InputCard>

          <InputCard
            v-if="!isEditMode"
            :label="
              $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.LABEL')
            "
            class="outbound_input_field mt_20"
          >
            <p class="segment_description mt_8">
              {{
                $t(
                  'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.DESCRIPTION'
                )
              }}
            </p>

            <woot-button
              class="setting_contact_btn my_8"
              color-scheme="primary"
              variant="smooth"
              @click="openContactSettingsModal"
            >
              {{
                $t(
                  'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.SETTING_BTN'
                )
              }}
            </woot-button>

            <!-- Display saved contact settings -->
            <div v-if="contactSettings" class="contact_settings_display mt_16">
              <div class="settings_row">
                <label class="settings_label text_12">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.ELIGIBLE_AUDIENCE'
                    )
                  }}:
                </label>
                <span class="settings_value text_14 font_600">
                  {{ eligibleAudience }}
                </span>
              </div>
              <div
                v-if="showAppliedFilters"
                class="settings_row filters_summary"
              >
                <label class="settings_label text_12">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.FILTERS_APPLIED'
                    )
                  }}:
                </label>
                <div class="settings_value text_14 font_600 filters_list">
                  <span v-if="contactSettings.appliedFilters.length === 1">
                    {{ getFilterSummary(contactSettings.appliedFilters[0]) }}
                  </span>
                  <ul v-else class="filters_items">
                    <li
                      v-for="(filter, i) in contactSettings.appliedFilters"
                      :key="i"
                    >
                      <span v-if="i > 0" class="query_operator">
                        {{ filter.query_operator.toUpperCase() }}
                      </span>
                      {{ getFilterSummary(filter) }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <p class="input_label mt_8">
              {{
                $t(
                  'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.LIMIT_PLACEHOLDER'
                )
              }}:
            </p>
            <InputField
              v-model.number="outboundCampaign.limitContactCount"
              type="number"
              :disabled="limitContactCountDisabled"
              :placeholder="
                $t(
                  'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.LIMIT_PLACEHOLDER'
                )
              "
              :error="
                !getNumberFieldError('limitContactCount') &&
                $v.outboundCampaign.limitContactCount.$dirty &&
                $v.outboundCampaign.limitContactCount.$error
                  ? $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CONTACT_CONFIG.LIMIT_ERROR'
                    )
                  : getNumberFieldError('limitContactCount')
              "
              @blur="$v.outboundCampaign.limitContactCount.$touch"
              @input="onLimitContactCountInput"
            />

            <MultiselectDropdown
              class-name-label="status_dropdown"
              :label="$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.INBOX.LABEL')"
              :error="
                $v.outboundCampaign.selectedInbox.$error &&
                $v.outboundCampaign.selectedInbox.$dirty
                  ? $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.INBOX.ERROR')
                  : ''
              "
            >
              <multiselect
                v-model="outboundCampaign.selectedInbox"
                class="column small-12"
                :options="callInboxes"
                :disabled="isEditMode"
                track-by="id"
                label="name"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :hide-selected="false"
                :placeholder="
                  $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.INBOX.PLACEHOLDER')
                "
                selected-label
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                :max-height="200"
              />
            </MultiselectDropdown>
          </InputCard>

          <InputCard
            :label="$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.AI_WORKER.SETTING')"
            class="outbound_input_field my_20"
          >
            <p class="input_label mt_8">
              {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.AI_WORKER.LABEL') }}:
            </p>
            <InputField
              v-model.number="outboundCampaign.aiWorkerCount"
              type="number"
              :disabled="isEditMode"
              :placeholder="
                $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.AI_WORKER.PLACEHOLDER')
              "
              :error="
                aiWorkerError ||
                (!getNumberFieldError('aiWorkerCount') &&
                  $v.outboundCampaign.aiWorkerCount.$dirty &&
                  $v.outboundCampaign.aiWorkerCount.$error)
                  ? $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.AI_WORKER.ERROR')
                  : getNumberFieldError('aiWorkerCount')
              "
              @blur="$v.outboundCampaign.aiWorkerCount.$touch"
            />
            <span v-if="aiWorkerError" class="error_text mt_6">
              {{ aiWorkerError }}
            </span>

            <MultiselectDropdown
              class-name-label="status_dropdown"
              :label="$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ASSISTANT.TITLE')"
              :error="
                $v.outboundCampaign.selectedAssistant.$error &&
                $v.outboundCampaign.selectedAssistant.$dirty
                  ? $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ASSISTANT.ERROR')
                  : ''
              "
            >
              <multiselect
                v-model="outboundCampaign.selectedAssistant"
                class="column small-12"
                :options="llmAssistants"
                :disabled="isEditMode"
                track-by="id"
                label="name"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :hide-selected="false"
                :placeholder="
                  $t(
                    'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ASSISTANT.PLACEHOLDER'
                  )
                "
                selected-label
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                :max-height="200"
              />
            </MultiselectDropdown>
          </InputCard>

          <InputCard
            :label="
              $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.LABEL')
            "
            class="outbound_input_field"
          >
            <div class="status_config_fields_wrapper">
              <div class="status_config">
                <label class="status_label text_12">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.STATUS_FOR_RETRY'
                    )
                  }}
                </label>
                <MultiselectDropdown
                  class-name-label="status_dropdown"
                  :error="
                    $v.outboundCampaign.statusForRetry.$error &&
                    $v.outboundCampaign.statusForRetry.$dirty
                      ? $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.REQUIRED'
                        )
                      : ''
                  "
                >
                  <multiselect
                    v-model="outboundCampaign.statusForRetry"
                    class="column small-12"
                    track-by="id"
                    label="name"
                    :options="statusOptions.retry"
                    :multiple="true"
                    :taggable="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :hide-selected="true"
                    :placeholder="
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.SELECT_PLACEHOLDER'
                      )
                    "
                    selected-label
                    :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                    :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                    @tag="onRetryStatusTag"
                  />
                </MultiselectDropdown>
              </div>

              <div class="status_config">
                <label class="status_label text_12">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.STATUS_FOR_SUCCESS'
                    )
                  }}
                </label>
                <MultiselectDropdown
                  class-name-label="status_dropdown"
                  :error="
                    $v.outboundCampaign.statusForSuccess.$error &&
                    $v.outboundCampaign.statusForSuccess.$dirty
                      ? $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.REQUIRED'
                        )
                      : ''
                  "
                >
                  <multiselect
                    v-model="outboundCampaign.statusForSuccess"
                    class="column small-12"
                    track-by="id"
                    label="name"
                    :options="statusOptions.success"
                    :multiple="true"
                    :taggable="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :hide-selected="true"
                    :placeholder="
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.SELECT_PLACEHOLDER'
                      )
                    "
                    selected-label
                    :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                    :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                    @tag="onSuccessStatusTag"
                  />
                </MultiselectDropdown>
              </div>

              <div class="status_config">
                <label class="status_label text_12">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.STATUS_FOR_REJECT'
                    )
                  }}
                </label>
                <MultiselectDropdown
                  class-name-label="status_dropdown"
                  :error="
                    $v.outboundCampaign.statusForReject.$error &&
                    $v.outboundCampaign.statusForReject.$dirty
                      ? $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.REQUIRED'
                        )
                      : ''
                  "
                >
                  <multiselect
                    v-model="outboundCampaign.statusForReject"
                    class="column small-12"
                    track-by="id"
                    label="name"
                    :options="statusOptions.reject"
                    :multiple="true"
                    :taggable="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :hide-selected="true"
                    :placeholder="
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.SELECT_PLACEHOLDER'
                      )
                    "
                    selected-label
                    :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                    :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                    @tag="onRejectStatusTag"
                  />
                </MultiselectDropdown>
              </div>

              <div class="status_config">
                <label
                  v-tooltip.top="
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.DQ_DESCRIPTION'
                    )
                  "
                  class="status_label text_12"
                >
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.STATUS_FOR_DQ'
                    )
                  }}
                </label>
                <MultiselectDropdown
                  class-name-label="status_dropdown"
                  :error="
                    $v.outboundCampaign.statusForDq.$error &&
                    $v.outboundCampaign.statusForDq.$dirty
                      ? $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.REQUIRED'
                        )
                      : ''
                  "
                >
                  <multiselect
                    v-model="outboundCampaign.statusForDq"
                    class="column small-12"
                    track-by="id"
                    label="name"
                    :options="statusOptions.dq"
                    :multiple="true"
                    :taggable="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :hide-selected="true"
                    :placeholder="
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.STATUS_CONFIG.SELECT_PLACEHOLDER'
                      )
                    "
                    selected-label
                    :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                    :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                    @tag="onDqStatusTag"
                  />
                </MultiselectDropdown>
              </div>
            </div>
          </InputCard>
        </aside>

        <aside>
          <InputCard
            :label="
              $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.RETRY_SETTINGS.LABEL')
            "
            class="outbound_input_field"
          >
            <div class="retry_settings_wrapper">
              <div class="retry_setting_field">
                <label class="input_label">
                  {{
                    $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.MAX_RETRY.LABEL')
                  }}
                </label>
                <InputField
                  v-model.number="outboundCampaign.maxRetry"
                  type="number"
                  :disabled="isEditMode"
                  :placeholder="
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.MAX_RETRY.PLACEHOLDER'
                    )
                  "
                  :error="
                    !getNumberFieldError('maxRetry') &&
                    $v.outboundCampaign.maxRetry.$dirty &&
                    $v.outboundCampaign.maxRetry.$error
                      ? $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.MAX_RETRY.ERROR'
                        )
                      : getNumberFieldError('maxRetry')
                  "
                  @blur="$v.outboundCampaign.maxRetry.$touch"
                />
              </div>
              <div class="retry_setting_field">
                <label class="input_label">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.RETRY_DURATION.LABEL'
                    )
                  }}
                </label>
                <InputField
                  v-model.number="outboundCampaign.retryDuration"
                  type="number"
                  :disabled="isEditMode"
                  :placeholder="
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.RETRY_DURATION.PLACEHOLDER'
                    )
                  "
                  :error="
                    !getNumberFieldError('retryDuration') &&
                    $v.outboundCampaign.retryDuration.$dirty &&
                    $v.outboundCampaign.retryDuration.$error
                      ? $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.RETRY_DURATION.ERROR'
                        )
                      : getNumberFieldError('retryDuration')
                  "
                  @blur="$v.outboundCampaign.retryDuration.$touch"
                />
              </div>
            </div>
          </InputCard>

          <InputCard
            :label="
              $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.SCHEDULE_SETTINGS.LABEL')
            "
            class="outbound_input_field"
          >
            <div class="schedule_settings_wrapper">
              <div class="schedule_setting_field">
                <label class="input_label">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CAMPAIGN_DURATION.LABEL'
                    )
                  }}
                </label>
                <div class="date_range_wrapper">
                  <DateRangePicker
                    class="font_normal"
                    :confirm-text="
                      $t('ORDER.LIST_TABLE.DATE_SELECT_CONFIRM_BTN')
                    "
                    :placeholder="
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CAMPAIGN_DURATION.PLACEHOLDER'
                      )
                    "
                    :value="outboundCampaign.campaignDuration"
                    :disabled="isEditMode"
                    :disabled-date="disableCampaignDate"
                    @change="onCampaignDurationChange"
                  />
                </div>
                <div
                  v-if="
                    $v.outboundCampaign.campaignDuration.$error &&
                      $v.outboundCampaign.campaignDuration.$dirty
                  "
                  class="subscription_error text_12 mt_6"
                >
                  <span class="error_text">{{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CAMPAIGN_DURATION.ERROR'
                    )
                  }}</span>
                </div>
                <div
                  v-if="subscriptionError"
                  class="subscription_error text_12 mt_6"
                >
                  <fluent-icon icon="warning" />
                  <span class="error_text">{{ subscriptionError }}</span>
                </div>
              </div>
              <div class="schedule_setting_field">
                <label class="input_label">
                  {{
                    $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.EXCLUDE_DAYS.LABEL')
                  }}
                </label>
                <div class="exclude_days_wrapper">
                  <label
                    v-for="day in daysOfWeek"
                    :key="day.value"
                    class="day_checkbox"
                  >
                    <input
                      v-model="outboundCampaign.excludeDays"
                      type="checkbox"
                      :value="day.value"
                      :disabled="isEditMode || isDaySelected(day.value)"
                      class="mb_0"
                    />
                    <span class="day_label">{{ day.label }}</span>
                  </label>
                </div>
                <div
                  v-if="excludeDaysError"
                  class="exclude_days_error text_12 mt_6"
                >
                  <span class="error_text">{{ excludeDaysError }}</span>
                </div>
              </div>
            </div>
          </InputCard>

          <InputCard
            :label="$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CALL_CONFIG.LABEL')"
            class="outbound_input_field mt_20"
          >
            <div class="call_config_fields_wrapper">
              <div class="call_config_field">
                <label class="input_label">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CALL_TIME_RANGE.LABEL'
                    )
                  }}
                </label>
                <div class="call_time_range_wrapper">
                  <div class="time_picker">
                    <woot-date-time-picker
                      :value="callTimeStartDate"
                      :disable-dates="0"
                      display-format="HH:mm"
                      type="time"
                      :disabled="isEditMode"
                      :placeholder="
                        $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CALL_TIME_RANGE.START'
                        )
                      "
                      :confirm-text="
                        $t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')
                      "
                      @change="onCallTimeStartChange"
                    />
                  </div>
                  <span class="time_separator">-</span>
                  <div class="time_picker">
                    <woot-date-time-picker
                      :value="callTimeEndDate"
                      :disable-dates="0"
                      display-format="HH:mm"
                      type="time"
                      :disabled="isEditMode"
                      :placeholder="
                        $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CALL_TIME_RANGE.END'
                        )
                      "
                      :confirm-text="
                        $t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')
                      "
                      @change="onCallTimeEndChange"
                    />
                  </div>
                </div>
                <div
                  v-if="
                    callTimeError ||
                      ($v.outboundCampaign.callTimeStart.$error &&
                        $v.outboundCampaign.callTimeStart.$dirty) ||
                      ($v.outboundCampaign.callTimeEnd.$error &&
                        $v.outboundCampaign.callTimeEnd.$dirty)
                  "
                  class="time_error text_12 mt_6"
                >
                  <span class="error_text">{{
                    callTimeError ||
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CALL_TIME_RANGE.REQUIRED'
                      )
                  }}</span>
                </div>
              </div>
              <div class="call_config_field">
                <label class="input_label">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.AVG_CALL_TIME.LABEL'
                    )
                  }}
                </label>
                <InputField
                  v-model.number="outboundCampaign.averageCallTime"
                  type="number"
                  :disabled="isEditMode"
                  :placeholder="
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.AVG_CALL_TIME.PLACEHOLDER'
                    )
                  "
                  :error="
                    !getNumberFieldError('averageCallTime') &&
                    $v.outboundCampaign.averageCallTime.$dirty &&
                    $v.outboundCampaign.averageCallTime.$error
                      ? $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.AVG_CALL_TIME.ERROR'
                        )
                      : getNumberFieldError('averageCallTime')
                  "
                  @blur="$v.outboundCampaign.averageCallTime.$touch"
                />
              </div>
              <div class="call_config_field">
                <label class="input_label">
                  {{
                    $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.WAIT_TIME.LABEL')
                  }}
                </label>
                <InputField
                  v-model.number="outboundCampaign.waitTime"
                  type="number"
                  :disabled="isEditMode"
                  :placeholder="
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.WAIT_TIME.PLACEHOLDER'
                    )
                  "
                  :error="
                    !getNumberFieldError('waitTime') &&
                    $v.outboundCampaign.waitTime.$dirty &&
                    $v.outboundCampaign.waitTime.$error
                      ? $t(
                          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.WAIT_TIME.ERROR'
                        )
                      : getNumberFieldError('waitTime')
                  "
                  @blur="$v.outboundCampaign.waitTime.$touch"
                />
              </div>
            </div>
          </InputCard>

          <section v-if="!isEditMode" class="estimated_cost_section">
            <InputCard
              :label="
                $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.LABEL')
              "
              class="outbound_input_field"
            >
              <div class="estimated_cost_wrapper">
                <div
                  v-if="estimatedCostData.isLoaded"
                  class="estimated_cost_content"
                >
                  <div class="cost_row">
                    <span class="cost_label text_12">{{
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.TOTAL_MINUTES'
                      )
                    }}</span>
                    <span class="cost_value text_14 font_600">{{
                      estimatedCostData.totalMinutes.toLocaleString()
                    }}</span>
                  </div>
                  <div class="cost_row">
                    <span class="cost_label text_12">{{
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.REQUIRED_WORKERS'
                      )
                    }}</span>
                    <span
                      class="cost_value text_14 font_600"
                      :class="{
                        cost_error: estimatedCostData.workersExceedLimit,
                      }"
                    >
                      {{ estimatedCostData.requiredWorkers }}
                    </span>
                  </div>
                  <div class="cost_row">
                    <span class="cost_label text_12">{{
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.AVAILABLE_WORKERS'
                      )
                    }}</span>
                    <span class="cost_value text_14 font_600">{{
                      estimatedCostData.availableWorkers
                    }}</span>
                  </div>
                  <div class="cost_row">
                    <span class="cost_label text_12">{{
                      $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.CAMPAIGN_DURATION_MONTHS'
                      )
                    }}</span>
                    <span class="cost_value text_14 font_600">{{
                      estimatedCostData.campaignDurationMonths.toFixed(1)
                    }}</span>
                  </div>

                  <div v-if="showWorkerWarning" class="cost_warning_box">
                    <fluent-icon icon="warning" />
                    <div class="warning_content">
                      <p class="warning_title text_14 font_600">
                        {{ $t(workerWarningTitle) }}
                      </p>
                      <p class="warning_message text_12">
                        {{ workerWarningMessage }}
                      </p>
                      <p
                        v-if="estimatedCostData.workersExceedLimit"
                        class="warning_support text_12"
                      >
                        {{
                          $t(
                            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.CONTACT_SUPPORT'
                          )
                        }}
                      </p>
                    </div>
                  </div>
                </div>
                <div v-else class="estimated_cost_placeholder">
                  {{
                    $t(
                      'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.FILL_REQUIRED_FIELDS'
                    )
                  }}
                </div>
              </div>
            </InputCard>
          </section>
        </aside>
      </section>

      <div class="outbound_campaign_add_section_footer my_20">
        <woot-button
          :is-loading="
            uiFlags.isCreating || uiFlags.isUpdating || isFetchingDetails
          "
          :disabled="isSubmitDisabled"
          @click="onSave"
        >
          {{ submitButtonText }}
        </woot-button>
      </div>
    </div>

    <!-- Contact Settings Modal -->
    <ContactSettingsModal
      :is-open="isContactSettingsModalOpen"
      :on-close="closeContactSettingsModal"
      :on-save="onContactSettingsSave"
      :prefill-data="contactSettings"
    />
  </div>
</template>

<script>
import InputField from 'components/ui/inputs/InputField.vue';
import { mapGetters } from 'vuex';
import { required, minValue } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import MultiselectDropdown from 'components/ui/inputs/MultiselectDropdown.vue';
import InputCard from 'components/ui/inputs/InputCard.vue';
import DateRangePicker from 'components/ui/DateRangePicker.vue';
import moment from 'moment-timezone';
import ContactSettingsModal from './ContactSettingsModal.vue';
import filterTypes from '../../../contacts/contactFilterItems';
import filterQueryGenerator from '../../../../../helper/filterQueryGenerator';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';

// Custom validator: no decimals allowed
const noDecimal = value => {
  if (value === null || value === undefined || value === '') return true;
  return !value.toString().includes('.');
};

export default {
  components: {
    InputField,
    MultiselectDropdown,
    InputCard,
    DateRangePicker,
    ContactSettingsModal,
    WootDateTimePicker,
  },
  mixins: [alertMixin],
  data() {
    return {
      isContactSettingsModalOpen: false,
      contactSettings: null,
      aiWorkerError: '',
      subscriptionError: '',
      callTimeError: '',
      serverErrorMessage: null,
      filtersFori18n: 'CONTACTS_FILTER',
      isEditMode: false,
      isFetchingDetails: false,
      hasManuallySetLimitContactCount: false,
      excludeDaysError: '',
      daysOfWeek: [
        { label: 'Sun', value: 'sun' },
        { label: 'Mon', value: 'mon' },
        { label: 'Tue', value: 'tue' },
        { label: 'Wed', value: 'wed' },
        { label: 'Thu', value: 'thu' },
        { label: 'Fri', value: 'fri' },
        { label: 'Sat', value: 'sat' },
      ],
      statusOptions: {
        retry: [{ id: 'retry', name: 'Retry' }],
        success: [
          { id: 'success', name: 'Success' },
          { id: 'kiv', name: 'KIV' },
        ],
        reject: [{ id: 'reject', name: 'Reject' }],
        dq: [{ id: 'dq', name: 'DQ' }],
      },
      outboundCampaign: {
        campaignName: '',
        limitContactCount: null,
        aiWorkerCount: null,
        campaignDuration: null,
        callTimeStart: null,
        callTimeEnd: null,
        averageCallTime: null,
        excludeDays: [],
        waitTime: null,
        statusForRetry: [],
        statusForSuccess: [],
        statusForReject: [],
        statusForDq: [],
        maxRetry: null,
        retryDuration: null,
        selectedInbox: null,
        selectedAssistant: null,
      },
      isInitialized: false,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      getAccount: 'accounts/getAccount',
      uiFlags: 'campaign_agents/getUIFlags',
      userPlan: 'accounts/getUserPlan',
      llmAssistants: 'llmbots/getLLMAssistants',
      callInboxes: 'llmbots/getCallInboxes',
      campaignAgentMeta: 'campaign_agents/getMeta',
    }),
    currentAccount() {
      return this.getAccount(this.currentUser.account_id);
    },
    aiWorkers() {
      return {
        ai_worker_number:
          this.campaignAgentMeta?.ai_workers?.ai_worker_number || 0,
        ai_worker_hours:
          this.campaignAgentMeta?.ai_workers?.ai_worker_hours || 0,
        ai_worker_day: this.campaignAgentMeta?.ai_workers?.ai_worker_day || 0,
      };
    },
    eligibleAudience() {
      return this.contactSettings &&
        this.contactSettings.eligible_audience &&
        this.contactSettings.eligible_audience.audience
        ? this.contactSettings.eligible_audience.audience.toLocaleString()
        : 0;
    },
    showAppliedFilters() {
      return (
        this.contactSettings &&
        this.contactSettings.appliedFilters &&
        this.contactSettings.appliedFilters.length > 0
      );
    },
    fTypes() {
      return filterTypes?.map(f => ({
        ...f,
        attributeName: this.$t(
          `CONTACTS_FILTER.ATTRIBUTES.${f.attributeI18nKey}`
        ),
      }));
    },
    estimatedCostData() {
      return this.calculateEstimatedCost();
    },
    isSubmitDisabled() {
      if (this.isEditMode) {
        // In edit mode, don't disable based on estimated cost
        return false;
      }
      const { workersExceedLimit, requiredWorkers } = this.estimatedCostData;
      return (
        workersExceedLimit ||
        (requiredWorkers > 0 &&
          this.outboundCampaign.aiWorkerCount < requiredWorkers)
      );
    },
    isAiWorkerInsufficient() {
      const { requiredWorkers, workersExceedLimit } = this.estimatedCostData;
      return (
        !workersExceedLimit &&
        requiredWorkers > 0 &&
        this.outboundCampaign.aiWorkerCount < requiredWorkers
      );
    },
    showWorkerWarning() {
      return (
        this.estimatedCostData.workersExceedLimit || this.isAiWorkerInsufficient
      );
    },
    workerWarningTitle() {
      return this.isAiWorkerInsufficient
        ? 'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.AI_WORKER_INSUFFICIENT_TITLE'
        : 'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.WORKER_LIMIT_EXCEEDED_TITLE';
    },
    workerWarningMessage() {
      const {
        requiredWorkers,
        availableWorkers,
        adjustedContactCount,
      } = this.estimatedCostData;

      if (this.isAiWorkerInsufficient) {
        return this.$t(
          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.AI_WORKER_INSUFFICIENT_MESSAGE',
          {
            required: requiredWorkers,
            current: this.outboundCampaign.aiWorkerCount,
          }
        );
      }

      return this.$t(
        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.ESTIMATED_COST.WORKER_LIMIT_EXCEEDED_MESSAGE',
        {
          required: requiredWorkers,
          available: availableWorkers,
          adjusted: adjustedContactCount.toLocaleString(),
        }
      );
    },
    isCostExceedsLimit() {
      return this.estimatedCostData.workersExceedLimit;
    },
    submitButtonText() {
      return this.isEditMode
        ? this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.SUBMIT_ACTION.UPDATE_CAMPAIGN'
          )
        : this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.SUBMIT_ACTION.BTN_LABEL'
          );
    },
    limitContactCountDisabled() {
      // Enable limit contact field by default (except in edit mode)
      // Users can proactively set a limit even if cost doesn't exceed limit
      return false;
    },
    callTimeStartDate() {
      if (!this.outboundCampaign.callTimeStart) return null;
      return this.parseTime(this.outboundCampaign.callTimeStart);
    },
    callTimeEndDate() {
      if (!this.outboundCampaign.callTimeEnd) return null;
      return this.parseTime(this.outboundCampaign.callTimeEnd);
    },
  },
  validations() {
    return {
      outboundCampaign: {
        campaignName: {
          required,
        },
        limitContactCount: {
          required: this.isEditMode ? value => !!value : required,
          minValue: minValue(1),
          noDecimal,
        },
        aiWorkerCount: {
          required,
          minValue: minValue(1),
          noDecimal,
          validateNumber: value =>
            value === null ||
            value === undefined ||
            value === '' ||
            !Number.isNaN(value),
        },
        averageCallTime: {
          required,
          minValue: minValue(1),
          noDecimal,
          validateNumber: value =>
            value === null ||
            value === undefined ||
            value === '' ||
            !Number.isNaN(value),
        },
        waitTime: {
          required,
          minValue: minValue(0),
          noDecimal,
          validateNumber: value =>
            value === null ||
            value === undefined ||
            value === '' ||
            !Number.isNaN(value),
        },
        maxRetry: {
          required,
          minValue: minValue(0),
          noDecimal,
          validateNumber: value =>
            value === null ||
            value === undefined ||
            value === '' ||
            !Number.isNaN(value),
        },
        retryDuration: {
          required,
          minValue: minValue(1),
          noDecimal,
          validateNumber: value =>
            value === null ||
            value === undefined ||
            value === '' ||
            !Number.isNaN(value),
        },
        statusForRetry: {
          required: value => Array.isArray(value) && value.length > 0,
        },
        statusForSuccess: {
          required: value => Array.isArray(value) && value.length > 0,
        },
        statusForReject: {
          required: value => Array.isArray(value) && value.length > 0,
        },
        statusForDq: {
          required: value => Array.isArray(value) && value.length > 0,
        },
        campaignDuration: {
          required: value => Array.isArray(value) && value.length === 2,
        },
        callTimeStart: {
          required,
        },
        callTimeEnd: {
          required,
        },
        selectedInbox: {
          required,
        },
        selectedAssistant: {
          required,
        },
      },
    };
  },
  watch: {
    aiWorkers: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.validateAIWorkerCount();
        }
      },
      immediate: true,
      deep: true,
    },
    'contactSettings.eligible_audience.audience': {
      handler(newVal) {
        if (newVal && newVal > 0 && !this.hasManuallySetLimitContactCount) {
          this.outboundCampaign.limitContactCount = newVal;
        }
      },
    },
    'outboundCampaign.excludeDays': {
      handler(newVal) {
        this.validateExcludeDays(newVal);
      },
      deep: true,
    },
    'outboundCampaign.callTimeStart': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.validateCallTimeRange();
        }
      },
    },
    'outboundCampaign.callTimeEnd': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.validateCallTimeRange();
        }
      },
    },
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          // Refetch campaign details when navigating between campaigns
          this.isEditMode = true;
          this.fetchCampaignDetails(newId);
        } else if (!newId) {
          // Reset form when navigating to add page (no id)
          this.resetForm();
        }
      },
      immediate: false,
    },
  },
  activated() {
    this.init();
  },
  mounted() {
    this.init();
  },
  deactivated() {
    this.$v.outboundCampaign.$reset();
  },
  methods: {
    init() {
      if (this.isInitialized) {
        return;
      }

      this.isInitialized = true;

      this.$store.dispatch('llmbots/fetchLLMAssistants', { type: 'agent' });
      this.$store.dispatch('llmbots/fetchCallInboxed');

      if (!this.campaignAgentMeta?.ai_workers) {
        this.$store.dispatch('campaign_agents/fetchCampaignAgent');
      }

      // check if there is param id on the url (edit page)
      const campaignId = this.$route.params.id;
      if (campaignId) {
        this.isEditMode = true;
        this.fetchCampaignDetails(campaignId);
      }
    },
    isDaySelected(day) {
      return (
        !this.outboundCampaign.excludeDays.includes(day) &&
        this.outboundCampaign.excludeDays.length >= 6
      );
    },
    getNumberFieldError(field) {
      const vField = this.$v.outboundCampaign[field];
      if (!vField || !vField.$dirty || !vField.$error) {
        return '';
      }
      if (vField.noDecimal === false) {
        const errorKeyMap = {
          limitContactCount: 'LIMIT_DECIMAL_ERROR',
          aiWorkerCount: 'DECIMAL_ERROR',
          averageCallTime: 'DECIMAL_ERROR',
          waitTime: 'DECIMAL_ERROR',
          maxRetry: 'DECIMAL_ERROR',
          retryDuration: 'DECIMAL_ERROR',
        };
        const sectionKeyMap = {
          limitContactCount: 'CONTACT_CONFIG',
          aiWorkerCount: 'AI_WORKER',
          averageCallTime: 'AVG_CALL_TIME',
          waitTime: 'WAIT_TIME',
          maxRetry: 'MAX_RETRY',
          retryDuration: 'RETRY_DURATION',
        };
        const section = sectionKeyMap[field];
        const key = errorKeyMap[field];
        return this.$t(`CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.${section}.${key}`);
      }
      return '';
    },
    async fetchCampaignDetails(campaignId) {
      this.isFetchingDetails = true;
      try {
        // Fetch required data first (inboxes and assistants)
        await Promise.all([
          this.$store.dispatch('llmbots/fetchCallInboxed'),
          this.$store.dispatch('llmbots/fetchLLMAssistants'),
        ]);

        const data = await this.$store.dispatch(
          'campaign_agents/getCampaignAgentDetail',
          campaignId
        );
        this.populateFormData(data);
      } catch (error) {
        console.error('Error fetching campaign details:', error);
        this.showAlert(
          error.response?.data?.message || 'Failed to load campaign details'
        );
      } finally {
        this.isFetchingDetails = false;
      }
    },
    populateFormData(data) {
      // Populate basic fields
      this.outboundCampaign.campaignName = data.name || '';
      this.outboundCampaign.limitContactCount =
        data.limit_contact_count || null;
      this.outboundCampaign.aiWorkerCount = data.worker_count || null;

      // Populate dates
      if (data.start_date && data.end_date) {
        this.outboundCampaign.campaignDuration = [
          moment(data.start_date).toDate(),
          moment(data.end_date).toDate(),
        ];
      }

      // Populate time range - convert stored UTC to Kuala Lumpur time.
      // data.time_start and data.time_end are ISO datetime strings.
      // The calling window is KL business hours, so display in KL regardless
      // of the browser's timezone.
      if (data.time_start) {
        this.outboundCampaign.callTimeStart = moment
          .utc(data.time_start)
          .tz('Asia/Kuala_Lumpur')
          .format('HH:mm');
      } else {
        this.outboundCampaign.callTimeStart = '';
      }

      if (data.time_end) {
        this.outboundCampaign.callTimeEnd = moment
          .utc(data.time_end)
          .tz('Asia/Kuala_Lumpur')
          .format('HH:mm');
      } else {
        this.outboundCampaign.callTimeEnd = '';
      }

      // Populate inbox and assistant - match by ID from store
      if (data.inbox_id) {
        this.outboundCampaign.selectedInbox = this.callInboxes?.find(
          inbox => inbox.id === data.inbox_id
        );
      }
      if (data.llm_chatbot_id) {
        this.outboundCampaign.selectedAssistant = this.llmAssistants?.find(
          assistant => assistant.id === data.llm_chatbot_id
        );
      }

      // Populate call settings from nested call_settings object
      const callSettings = data.call_settings || {};
      this.outboundCampaign.averageCallTime =
        callSettings.average_call_time || null;
      this.outboundCampaign.waitTime = callSettings.wait_time_seconds
        ? Math.floor(callSettings.wait_time_seconds / 60)
        : null;
      this.outboundCampaign.maxRetry = callSettings.max_retry_attempts || null;
      this.outboundCampaign.retryDuration =
        callSettings.retry_interval_hours || null;
      this.outboundCampaign.excludeDays = callSettings.exclude_days || [];

      // Populate status configurations
      this.outboundCampaign.statusForRetry = Array.isArray(
        callSettings.status_retry
      )
        ? callSettings.status_retry.map(s => ({ id: s, name: s }))
        : [];
      this.outboundCampaign.statusForSuccess = Array.isArray(
        callSettings.status_success
      )
        ? callSettings.status_success.map(s => ({ id: s, name: s }))
        : [];
      this.outboundCampaign.statusForReject = Array.isArray(
        callSettings.status_reject
      )
        ? callSettings.status_reject.map(s => ({ id: s, name: s }))
        : [];
      this.outboundCampaign.statusForDq = Array.isArray(callSettings.status_dq)
        ? callSettings.status_dq.map(s => ({ id: s, name: s }))
        : [];

      // Populate contact settings (audience filters)
      if (data.audience) {
        this.contactSettings = {
          eligible_audience: {
            audience: data.audience_count || 0,
          },
          appliedFilters: data.audience_filters || [],
        };
        // In edit mode, if limit_contact_count is set, use it and mark as manually set
        if (this.isEditMode && data.limit_contact_count) {
          this.outboundCampaign.limitContactCount = data.limit_contact_count;
          this.hasManuallySetLimitContactCount = true;
        }
        // Prefill limit contact count with eligible audience count in edit mode if not set
        if (this.isEditMode && !this.outboundCampaign.limitContactCount) {
          this.outboundCampaign.limitContactCount = data.audience_count || null;
        }
      }
    },
    openContactSettingsModal() {
      this.isContactSettingsModalOpen = true;
    },
    closeContactSettingsModal() {
      this.isContactSettingsModalOpen = false;
    },
    onContactSettingsSave(settings) {
      this.contactSettings = settings;
      this.closeContactSettingsModal();
    },
    onLimitContactCountInput() {
      this.hasManuallySetLimitContactCount = true;
    },
    disableCampaignDate(date) {
      if (!date) {
        return false;
      }

      const today = moment().local();
      const theDate = moment(date).local();

      const subscriptionEndDate = this.userPlan?.subscription?.end_date
        ? moment(this.userPlan.subscription?.end_date).local()
        : null;

      if (subscriptionEndDate) {
        return (
          theDate.isAfter(subscriptionEndDate, 'day') ||
          theDate.isBefore(today, 'day')
        );
      }

      return theDate.isBefore(today, 'day');
    },
    onCampaignDurationChange(dateRange) {
      this.outboundCampaign.campaignDuration = dateRange;
      this.validateCampaignDuration();
    },
    validateCampaignDuration() {
      this.subscriptionError = '';

      if (
        !this.outboundCampaign.campaignDuration ||
        this.outboundCampaign.campaignDuration.length !== 2
      ) {
        return;
      }

      const subscriptionEndDate = this.currentAccount?.subscription_end_date
        ? moment(this.currentAccount.subscription_end_date).local()
        : null;

      if (subscriptionEndDate) {
        const endDate = moment(
          this.outboundCampaign.campaignDuration[1]
        ).local();

        if (endDate.isAfter(subscriptionEndDate, 'day')) {
          this.subscriptionError = this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CAMPAIGN_DURATION.SUBSCRIPTION_ERROR'
          );
        }
      }
    },
    validateCallTimeRange() {
      this.callTimeError = '';

      if (
        !this.outboundCampaign.callTimeStart ||
        !this.outboundCampaign.callTimeEnd
      ) {
        return;
      }

      const start = this.parseTime(this.outboundCampaign.callTimeStart);
      const end = this.parseTime(this.outboundCampaign.callTimeEnd);
      const MAX_CALL_HOURS = this.aiWorkers.ai_worker_hours
        ? this.currentAccount.limits.ai_worker_hours
        : 8;

      const durationHours = (end - start) / (1000 * 60 * 60);

      if (durationHours > MAX_CALL_HOURS) {
        this.callTimeError = this.$t(
          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CALL_TIME_RANGE.MAX_ERROR',
          {
            max: MAX_CALL_HOURS,
          }
        );
      } else if (durationHours <= 0) {
        this.callTimeError = this.$t(
          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.CALL_TIME_RANGE.INVALID_ERROR'
        );
      }
    },
    validateExcludeDays(selectedDays) {
      this.excludeDaysError = '';
      if (Array.isArray(selectedDays) && selectedDays.length >= 7) {
        this.excludeDaysError = this.$t(
          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.EXCLUDE_DAYS.ALL_DAYS_ERROR'
        );
        // Remove the last added day to prevent selecting all 7
        this.outboundCampaign.excludeDays = selectedDays.slice(0, 6);
      }
    },
    parseTime(timeString) {
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    },
    onCallTimeStartChange(date) {
      this.outboundCampaign.callTimeStart = date ? this.formatTime(date) : null;
    },
    onCallTimeEndChange(date) {
      this.outboundCampaign.callTimeEnd = date ? this.formatTime(date) : null;
    },
    formatTime(date) {
      if (!date) return null;
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
    },
    /**
     * Convert Kuala Lumpur time (HH:mm) with a date to UTC time string (HH:mm).
     * The calling window is defined as KL business hours, so the picked time is
     * interpreted in Asia/Kuala_Lumpur regardless of the browser's timezone.
     * @param {Date|string} date - The campaign date
     * @param {string} localTime - Kuala Lumpur time in HH:mm format
     * @returns {string} UTC time in HH:mm format
     */
    convertLocalToUTC(date, localTime) {
      const dateStr = moment(date).format('YYYY-MM-DD');

      // Interpret the picked time as Kuala Lumpur wall-clock, then convert to UTC.
      const klMoment = moment.tz(
        `${dateStr} ${localTime}`,
        'YYYY-MM-DD HH:mm',
        'Asia/Kuala_Lumpur'
      );

      return klMoment.utc().format('HH:mm');
    },
    /**
     * Convert UTC time (HH:mm) with a date to Kuala Lumpur time string (HH:mm).
     * @param {Date|string} date - The campaign date
     * @param {string} utcTime - UTC time in HH:mm format
     * @returns {string} Kuala Lumpur time in HH:mm format
     */
    convertUTCToLocal(date, utcTime) {
      const dateStr = moment(date).format('YYYY-MM-DD');

      // Convert the stored UTC time to Kuala Lumpur wall-clock for display.
      const utcMoment = moment.utc(`${dateStr} ${utcTime}`, 'YYYY-MM-DD HH:mm');

      return utcMoment.tz('Asia/Kuala_Lumpur').format('HH:mm');
    },
    validateAIWorkerCount() {
      this.aiWorkerError = '';

      if (!this.aiWorkers.ai_worker_number) {
        return;
      }

      const maxWorkers = this.aiWorkers.ai_worker_number;

      if (maxWorkers > 0 && this.outboundCampaign.aiWorkerCount > maxWorkers) {
        this.aiWorkerError = this.$t(
          'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.AI_WORKER.PLAN_LIMIT_EXCEEDED',
          {
            max: maxWorkers,
          }
        );
      } else {
        this.aiWorkerError = '';
      }
    },
    onRetryStatusTag(newTag) {
      const tag = { id: newTag.toLowerCase(), name: newTag };
      this.statusOptions.retry.push(tag);
      return tag;
    },
    onSuccessStatusTag(newTag) {
      const tag = { id: newTag.toLowerCase(), name: newTag };
      this.statusOptions.success.push(tag);
      return tag;
    },
    onRejectStatusTag(newTag) {
      const tag = { id: newTag.toLowerCase(), name: newTag };
      this.statusOptions.reject.push(tag);
      return tag;
    },
    onDqStatusTag(newTag) {
      const tag = { id: newTag.toLowerCase(), name: newTag };
      this.statusOptions.dq.push(tag);
      return tag;
    },
    calculateEstimatedCost() {
      // Default return object
      const defaultResult = {
        isLoaded: false,
        totalMinutes: 0,
        requiredWorkers: 0,
        availableWorkers: 0,
        workersExceedLimit: false,
        campaignDurationMonths: 0,
        adjustedContactCount: 0,
      };

      // Check if required data is available
      if (
        !this.outboundCampaign ||
        !this.currentAccount?.limits ||
        !this.outboundCampaign.campaignDuration ||
        this.outboundCampaign.campaignDuration.length !== 2
      ) {
        return defaultResult;
      }

      const contactCount = this.outboundCampaign.limitContactCount;
      const averageCallTime = this.outboundCampaign.averageCallTime;
      const maxRetry = this.outboundCampaign.maxRetry;
      const waitTime = this.outboundCampaign.waitTime;
      const ringTime = 1; // 1 minute ring time (fixed as per stakeholder)

      if (
        !contactCount ||
        !averageCallTime ||
        maxRetry === undefined ||
        maxRetry === null ||
        !waitTime
      ) {
        return defaultResult;
      }

      // Average pickup rate - using 50% as per stakeholder example
      const avgPickupRate = 0.5;
      const pickupCount = Math.round(contactCount * avgPickupRate);
      const noPickupCount = contactCount - pickupCount;

      // Calculate total minutes required
      // Ring time: all contacts
      const ringTimeMinutes = contactCount * ringTime;

      // Call time for people who pickup: 50% X 3 mins
      const callTimeForPickup = pickupCount * averageCallTime;

      // Wait time for retry (balance): 50% X 4 X 1 min
      const waitTimeForRetry = noPickupCount * maxRetry * waitTime;

      // Call time for retry: 50% X 3 mins
      const callTimeForRetry = noPickupCount * averageCallTime;

      // Total required time = ringTime + callTimeForPickup + waitTimeForRetry + callTimeForRetry
      const totalMinutesRequired =
        ringTimeMinutes +
        callTimeForPickup +
        waitTimeForRetry +
        callTimeForRetry;

      // Get available workers from account limits
      const availableWorkers = this.aiWorkers.ai_worker_number || 0;

      // Calculate campaign duration in months
      const startDate = moment(
        this.outboundCampaign.campaignDuration[0]
      ).local();
      const endDate = moment(this.outboundCampaign.campaignDuration[1]).local();

      // Calculate working days (excluding excludeDays)
      const excludeDaysMap = {
        sun: 0,
        mon: 1,
        tue: 2,
        wed: 3,
        thu: 4,
        fri: 5,
        sat: 6,
      };
      const excludedDayNumbers = (this.outboundCampaign.excludeDays || []).map(
        day => excludeDaysMap[day]
      );

      let workingDaysCount = 0;
      let currentDay = moment(startDate);
      while (currentDay.isSameOrBefore(endDate, 'day')) {
        if (!excludedDayNumbers.includes(currentDay.day())) {
          workingDaysCount += 1;
        }
        currentDay.add(1, 'day');
      }

      const campaignDurationMonths =
        workingDaysCount > 0 ? workingDaysCount / 20 : 0;

      // Calculate required workers
      // Workload = totalMinutesRequired / (1 worker time in a month)
      const workerTimeInMonth = 60 * 8 * 20; // 1 worker X 60 mins X 8 hours X 20 days
      const requiredWorkers = Math.ceil(
        totalMinutesRequired / workerTimeInMonth
      );

      // Check if required workers exceed available
      const workersExceedLimit = requiredWorkers > availableWorkers;

      // Calculate adjusted contact count for display (not auto-setting)
      let adjustedContactCount = contactCount;
      if (workersExceedLimit && availableWorkers > 0) {
        // Calculate max contact count that fits within available workers
        const maxTotalMinutes =
          availableWorkers * workerTimeInMonth * campaignDurationMonths;
        adjustedContactCount = Math.floor(
          maxTotalMinutes /
            (ringTime +
              avgPickupRate * averageCallTime +
              (1 - avgPickupRate) * maxRetry * waitTime +
              (1 - avgPickupRate) * averageCallTime)
        );
      }

      return {
        isLoaded: true,
        totalMinutes: totalMinutesRequired,
        requiredWorkers,
        availableWorkers,
        workersExceedLimit,
        campaignDurationMonths,
        adjustedContactCount,
      };
    },
    async onSave() {
      if (this.isEditMode) {
        // In edit mode, only validate status configuration fields
        this.$v.outboundCampaign.statusForRetry.$touch();
        this.$v.outboundCampaign.statusForSuccess.$touch();
        this.$v.outboundCampaign.statusForReject.$touch();
        this.$v.outboundCampaign.statusForDq.$touch();

        const hasError =
          this.$v.outboundCampaign.statusForRetry.$error ||
          this.$v.outboundCampaign.statusForSuccess.$error ||
          this.$v.outboundCampaign.statusForReject.$error ||
          this.$v.outboundCampaign.statusForDq.$error;

        if (hasError) {
          return;
        }
      } else {
        // In create mode, validate all fields
        this.$v.outboundCampaign.$touch();

        if (this.$v.outboundCampaign.$invalid) {
          return;
        }

        if (!this.contactSettings || !this.contactSettings.appliedFilters) {
          return;
        }

        if (this.aiWorkerError) {
          return;
        }

        if (this.callTimeError) {
          return;
        }

        if (this.subscriptionError) {
          return;
        }

        if (this.excludeDaysError) {
          return;
        }
      }

      try {
        if (this.isEditMode) {
          // Update existing campaign - only allow updating status configurations
          const updatePayload = {
            status_retry: this.outboundCampaign.statusForRetry.map(s => s.id),
            status_success: this.outboundCampaign.statusForSuccess.map(
              s => s.id
            ),
            status_reject: this.outboundCampaign.statusForReject.map(s => s.id),
            status_dq: this.outboundCampaign.statusForDq.map(s => s.id),
          };
          const response = await this.$store.dispatch(
            'campaign_agents/updateCampaignAgent',
            {
              id: this.$route.params.id,
              payload: updatePayload,
            }
          );

          // Check for server validation errors
          if (response?.errors) {
            this.serverErrorMessage = response.errors;
            return;
          }

          this.showAlert(
            this.$t(
              'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.SUBMIT_ACTION.UPDATE_SUCCESS'
            )
          );
        } else {
          // Create new campaign
          const audiencePayload = filterQueryGenerator(
            this.contactSettings.appliedFilters || []
          ).payload;

          // Convert local time to UTC for server
          const startTimeUTC = this.convertLocalToUTC(
            this.outboundCampaign.campaignDuration[0],
            this.outboundCampaign.callTimeStart
          );
          const endTimeUTC = this.convertLocalToUTC(
            this.outboundCampaign.campaignDuration[0],
            this.outboundCampaign.callTimeEnd
          );

          const payload = {
            description: '',
            llm_chatbot_id: this.outboundCampaign.selectedAssistant.id,
            inbox_id: this.outboundCampaign.selectedInbox.id,
            name: this.outboundCampaign.campaignName,
            start_date: moment(
              this.outboundCampaign.campaignDuration[0]
            ).format('YYYY-MM-DD'),
            end_date: moment(this.outboundCampaign.campaignDuration[1]).format(
              'YYYY-MM-DD'
            ),
            time_start: startTimeUTC,
            time_end: endTimeUTC,
            worker_count: this.outboundCampaign.aiWorkerCount,
            average_call_time: this.outboundCampaign.averageCallTime,
            wait_time_seconds: this.outboundCampaign.waitTime * 60,
            max_retry_attempts: this.outboundCampaign.maxRetry,
            retry_interval_hours: this.outboundCampaign.retryDuration,
            exclude_days: this.outboundCampaign.excludeDays,
            status_retry: this.outboundCampaign.statusForRetry.map(s => s.id),
            status_success: this.outboundCampaign.statusForSuccess.map(
              s => s.id
            ),
            status_reject: this.outboundCampaign.statusForReject.map(s => s.id),
            status_dq: this.outboundCampaign.statusForDq.map(s => s.id),
            audience: audiencePayload,
            limit_contact_count: this.outboundCampaign.limitContactCount,
          };
          const response = await this.$store.dispatch(
            'campaign_agents/createCampaignAgent',
            payload
          );

          // Check for server validation errors
          if (response?.errors) {
            this.serverErrorMessage = response.errors;
            return;
          }

          this.showAlert(
            this.$t(
              'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.SUBMIT_ACTION.CREATE_SUCCESS'
            )
          );
        }
        this.resetForm();
        this.$router.push({ name: 'outbound_call_campaigns' });
      } catch (error) {
        console.error('Error saving outbound call campaign:', error);

        // Handle server validation errors
        if (error.response?.data?.errors) {
          this.serverErrorMessage = error.response.data.errors;
          return;
        }

        this.showAlert(
          error.response?.data?.message ||
            this.$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.ADD.SUBMIT_ACTION.ERROR')
        );
      }
    },
    resetForm() {
      // Reset all form fields to initial state
      this.isEditMode = false;
      this.contactSettings = null;
      this.aiWorkerError = '';
      this.subscriptionError = '';
      this.callTimeError = '';
      this.excludeDaysError = '';
      this.serverErrorMessage = null;
      this.hasManuallySetLimitContactCount = false;
      this.isContactSettingsModalOpen = false;
      this.outboundCampaign = {
        campaignName: '',
        limitContactCount: null,
        aiWorkerCount: null,
        campaignDuration: null,
        callTimeStart: null,
        callTimeEnd: null,
        averageCallTime: null,
        excludeDays: [],
        waitTime: null,
        statusForRetry: [],
        statusForSuccess: [],
        statusForReject: [],
        statusForDq: [],
        maxRetry: null,
        retryDuration: null,
        selectedInbox: null,
        selectedAssistant: null,
      };
      // Reset vuelidate
      this.$v.outboundCampaign.$reset();
    },
    getFilterSummary(filter) {
      const attr = this.fTypes.find(
        f => f.attributeKey === filter.attribute_key
      );
      const attrName = attr ? attr.attributeName : filter.attribute_key;
      const attrFilter = this.fTypes.find(
        f => f.attributeKey === filter.attribute_key
      );
      const operator = attrFilter
        ? attrFilter.filterOperators.find(
            o => o.value === filter.filter_operator
          )
        : null;
      const operatorLabel = operator
        ? this.$t(`${this.filtersFori18n}.OPERATOR_LABELS.${operator.value}`)
        : filter.filter_operator;
      let valueDisplay = filter.values;

      if (Array.isArray(filter.values)) {
        valueDisplay = filter.values.map(v => v.name || v).join(', ');
      } else if (typeof filter.values === 'object' && filter.values !== null) {
        valueDisplay = filter.values.name || JSON.stringify(filter.values);
      }

      return `${attrName} ${operatorLabel} ${valueDisplay}`;
    },
  },
};
</script>

<style lang="scss">
.add_outbound_campaign_page {
  .dropdown_slot {
    padding: 0 !important;
  }

  .multiselect__input,
  .multiselect__tags .multiselect__input {
    height: 0 !important;
    visibility: hidden !important;
  }

  .multiselect--active .multiselect__input,
  .multiselect--active .multiselect__tags .multiselect__input {
    padding: 10px 3px !important;
    height: 100% !important;
    visibility: visible !important;
  }
}

.call_time_range_wrapper
  .time_picker
  .date-picker
  .mx-datepicker
  .mx-input-wrapper
  .mx-input {
  margin-bottom: 0 !important;
  margin-top: 8px;
}
</style>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/widgets/_fonts.scss';
@import '~dashboard/assets/scss/woot';

.bg_page {
  background-color: #fff;
}

.server_error_alert {
  background-color: var(--r-50);
  border: 1px solid var(--r-200);
  border-radius: 4px;
  margin-bottom: 16px;
  padding: 12px;

  .error_alert_content {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .error_messages {
    flex: 1;
  }

  .error_text {
    color: var(--r-600);
    font-size: 14px;
    margin: 0;
  }

  .error_list {
    margin: 0;
    padding-left: 16px;
    color: var(--r-600);
    font-size: 14px;

    li {
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.outbound_campaign_add_section {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  @include breakpoint(medium up) {
    grid-template-columns: 1fr 1fr;
  }
}

.segment_description {
  font-size: 12px;
  color: var(--s-600);
  margin-bottom: 8px;
}

.audience_info {
  padding: 12px;
  background-color: var(--s-50);
  border-radius: 4px;
  margin-bottom: 12px;
}

.audience_count {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.setting_contact_btn {
  width: fit-content;
  justify-content: center;
}

.contact_settings_display {
  padding: 12px;
  background-color: var(--s-50);
  border-radius: 4px;
}

.settings_row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--s-100);

  &:last-child {
    border-bottom: none;
  }

  &.filters_summary {
    flex-direction: column;
    align-items: flex-start;
  }
}

.settings_label {
  font-weight: 500;
  color: var(--s-600);
}

.settings_value {
  color: var(--s-900);
}

.error_text {
  color: var(--r-500);
}

.date_range_wrapper {
  width: 100%;
}

.call_time_range_wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.time_picker {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.time_label {
  font-weight: 500;
  color: var(--s-700);
}

.time_input {
  padding: 8px 12px;
  border: 1px solid var(--s-100);
  border-radius: 4px;
  font-size: 14px;
  color: var(--s-900);

  &:focus {
    outline: none;
    border-color: var(--w-500);
  }
}

.time_separator {
  font-size: 16px;
  color: var(--s-600);
}

.exclude_days_wrapper {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.day_checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid var(--s-100);
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: var(--s-50);
  }

  input[type='checkbox'] {
    cursor: pointer;
  }

  .day_label {
    font-size: 12px;
    color: var(--s-700);
    text-transform: uppercase;
  }
}

.status_config_section {
  width: 100%;
}

.status_config_fields_wrapper {
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  gap: 4px;

  @include breakpoint(medium up) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;

    .status_config {
      flex: 1;
      min-width: 200px;
    }
  }
}

.retry_settings_wrapper {
  display: flex;
  gap: 16px;
  padding-top: 8px;

  @include breakpoint(small down) {
    flex-direction: column;
  }
}

.retry_setting_field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.schedule_settings_wrapper {
  display: flex;
  gap: 16px;
  margin-top: 8px;

  @include breakpoint(small down) {
    flex-direction: column;
  }
}

.schedule_setting_field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.call_config_fields_wrapper {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 8px;

  @include breakpoint(small down) {
    grid-template-columns: 1fr;
  }
}

.call_config_field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status_config {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.status_label {
  font-weight: 500;
  color: var(--s-700);
  margin-bottom: 4px;
}

.status_description {
  font-size: 10px;
  color: var(--s-600);
  margin-top: -4px;
  margin-bottom: 8px;
}

.outbound_campaign_add_section_footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 0;
}

.subscription_error,
.time_error,
.exclude_days_error {
  display: flex;
  align-items: center;
  gap: 6px;

  .error_text {
    color: var(--r-500);
  }
}

.estimated_cost_section {
  margin-top: 20px;
}

.estimated_cost_wrapper {
  padding: 16px 0;
}

.estimated_cost_content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cost_row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: var(--s-50);
  border-radius: 4px;
}

.cost_label {
  font-weight: 500;
  color: var(--s-600);
}

.cost_value {
  color: var(--s-900);

  &.cost_error {
    color: var(--r-500);
  }
}

.estimated_cost_placeholder {
  padding: 16px;
  text-align: center;
  color: var(--s-600);
  font-size: 14px;
  background-color: var(--s-50);
  border-radius: 4px;
}

.cost_warning_box {
  display: flex;
  gap: 12px;
  padding: 16px;
  margin-top: 8px;
  background-color: var(--r-50);
  border: 1px solid var(--r-200);
  border-radius: 4px;

  .warning_content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .warning_title {
    color: var(--r-600);
  }

  .warning_message {
    color: var(--s-800);
    line-height: 1.5;
  }

  .warning_support {
    color: var(--s-600);
  }
}
</style>

<style>
.add_outbound_campaign_page .status_dropdown .message {
  margin-top: 0;
}
</style>
